import { useState, useMemo } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerTable } from '../../components/common/Shimmer';
import {
  FiRefreshCw, FiTrash2, FiSearch, FiX,
  FiZap, FiEye, FiEyeOff,
} from "react-icons/fi";
import { SiNotion } from "react-icons/si";
import StatusBadge from "../../components/common/StatusBadge";
import Pagination from "../../components/common/Pagination";
import toast from "react-hot-toast";

const MOCK_PAGES = [
  { id: 1, title: "Product Roadmap",     type: "Page",     workspace: "My Workspace", status: "Trained",  lastTrained: "2 days ago" },
  { id: 2, title: "Help Center Docs",    type: "Database", workspace: "My Workspace", status: "Trained",  lastTrained: "1 day ago"  },
  { id: 3, title: "Onboarding Guide",    type: "Page",     workspace: "My Workspace", status: "Pending",  lastTrained: "—"          },
  { id: 4, title: "Pricing FAQ",         type: "Page",     workspace: "My Workspace", status: "Trained",  lastTrained: "3 days ago" },
  { id: 5, title: "API Reference",       type: "Database", workspace: "My Workspace", status: "Pending",  lastTrained: "—"          },
  { id: 6, title: "Release Notes Q1",    type: "Page",     workspace: "My Workspace", status: "Trained",  lastTrained: "5 days ago" },
  { id: 7, title: "Support Playbook",    type: "Page",     workspace: "My Workspace", status: "Failed",   lastTrained: "4 days ago" },
];

const PAGE_SIZE = 8;

function ContentPageSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <ShimmerStatCard key={i} />)}
      </div>
      <div className="flex items-center gap-3">
        <ShimmerBox h="h-9" w="w-56" rounded="rounded-lg" />
        <div className="flex gap-2">
          {[...Array(4)].map((_, i) => <ShimmerBox key={i} h="h-8" w="w-20" rounded="rounded-lg" />)}
        </div>
      </div>
      <ShimmerTable rows={8} cols={6} />
    </div>
  );
}

export default function NotionContent() {
  const [token, setToken] = useState("");
  const loading = usePageLoader();
  const [showToken, setShowToken] = useState(false);
  const [connected, setConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pages, setPages] = useState(MOCK_PAGES);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  /* ── Derived ── */
  const counts = useMemo(() => ({
    total:   pages.length,
    trained: pages.filter(p => p.status === "Trained").length,
    pending: pages.filter(p => p.status === "Pending").length,
    failed:  pages.filter(p => p.status === "Failed").length,
  }), [pages]);

  const filtered = useMemo(() => pages.filter(p => {
    const okFilter = filter === "all" || p.status === filter;
    const okSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.type.toLowerCase().includes(search.toLowerCase());
    return okFilter && okSearch;
  }), [pages, filter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageIds    = paginated.map(p => p.id);
  const allPageSel = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

  const changeFilter = (f) => { setFilter(f); setPage(1); setSelectedIds([]); };
  const changeSearch = (s) => { setSearch(s); setPage(1); };
  const toggleSel    = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllSel = () => {
    if (allPageSel) setSelectedIds(p => p.filter(id => !pageIds.includes(id)));
    else setSelectedIds(p => [...new Set([...p, ...pageIds])]);
  };

  const simulateRetrain = (ids, cb) => {
    setPages(p => p.map(pg => ids.includes(pg.id) ? { ...pg, status: "Processing" } : pg));
    setTimeout(() => {
      setPages(p => p.map(pg => ids.includes(pg.id) ? { ...pg, status: "Trained", lastTrained: "Just now" } : pg));
      cb?.();
    }, 2000);
  };

  /* ── Handlers ── */
  const handleConnect = () => {
    if (!token.trim()) { toast.error("Please enter your Notion integration token."); return; }
    setConnected(true);
    toast.success("Notion workspace connected!");
  };

  const handleSync = async () => {
    setSyncing(true);
    await new Promise(r => setTimeout(r, 1400));
    setSyncing(false);
    toast.success("Pages synced from Notion.");
  };

  const handleDisconnect = () => {
    setConnected(false); setToken("");
    toast("Notion disconnected.", { icon: "ℹ️" });
  };

  const handleDelete = (id) => {
    setPages(p => p.filter(pg => pg.id !== id));
    setSelectedIds(p => p.filter(x => x !== id));
    toast.success("Page removed.");
  };

  const handleRetrain = (id) => simulateRetrain([id], () => toast.success("Page retrained."));

  const handleBulkDelete = () => {
    const n = selectedIds.length;
    setPages(p => p.filter(pg => !selectedIds.includes(pg.id)));
    setSelectedIds([]); toast.success(`${n} page${n > 1 ? "s" : ""} deleted.`);
  };

  const handleBulkRetrain = () => {
    const ids = [...selectedIds];
    simulateRetrain(ids, () => { setSelectedIds([]); toast.success(`${ids.length} page${ids.length > 1 ? "s" : ""} retrained.`); });
  };

  const handleTrainAllPending = () => {
    const ids = pages.filter(p => p.status === "Pending").map(p => p.id);
    if (!ids.length) return;
    simulateRetrain(ids, () => toast.success(`${ids.length} pending page${ids.length > 1 ? "s" : ""} trained.`));
  };

  /* ── Not connected ── */
  if (!connected) {
    return (
      <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>
        <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center gap-3">
          <SiNotion size={20} className="text-gray-800 shrink-0" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Notion</h1>
            <p className="text-sm text-gray-500 mt-0.5">Connect your workspace to train on Notion pages and databases</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white border rounded-2xl p-8 w-full max-w-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center shrink-0">
                <SiNotion size={22} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Connect Notion</h2>
                <p className="text-sm text-gray-500">Train your chatbot on Notion content</p>
              </div>
            </div>

            <ol className="text-sm text-gray-600 space-y-2 mb-6 list-decimal list-inside bg-gray-50 rounded-xl p-4">
              <li>Go to <b>notion.so/my-integrations</b> and create a new integration.</li>
              <li>Copy the <b>Internal Integration Token</b>.</li>
              <li>Share your pages with the integration.</li>
              <li>Paste the token below.</li>
            </ol>

            <label className="block text-sm font-medium text-gray-700 mb-1">Integration Token</label>
            <div className="relative mb-4">
              <input
                type={showToken ? "text" : "password"}
                placeholder="secret_xxxxxxxxxxxxxxxxxxxx"
                value={token}
                onChange={e => setToken(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleConnect()}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 pr-10"
              />
              <button onClick={() => setShowToken(!showToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showToken ? <FiEyeOff size={15} /> : <FiEye size={15} />}
              </button>
            </div>
            <button onClick={handleConnect} className="w-full bg-primary text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition">
              Connect Notion
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Connected ── */
  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* Sticky header */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center shrink-0">
            <SiNotion size={16} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900">Notion</h1>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Connected</span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{counts.total} pages · {counts.trained} trained</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {counts.pending > 0 && (
            <button onClick={handleTrainAllPending} className="flex items-center gap-2 text-sm border border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary/5 transition">
              <FiZap size={14} /> Train {counts.pending} Pending
            </button>
          )}
          <button onClick={handleSync} disabled={syncing} className="flex items-center gap-2 text-sm bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-70 transition">
            {syncing ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiRefreshCw size={14} />}
            {syncing ? "Syncing…" : "Sync Pages"}
          </button>
          <button onClick={handleDisconnect} className="text-xs text-gray-500 hover:text-red-500 px-3 py-2 border rounded-lg hover:bg-gray-50 transition">
            Disconnect
          </button>
        </div>
      </div>

      {loading ? <ContentPageSkeleton /> : <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-4">

          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard label="Total Pages"  value={counts.total}   color="text-gray-900" />
            <KpiCard label="Trained"      value={counts.trained} color="text-green-600" dot="bg-green-500" />
            <KpiCard label="Pending / Failed" value={`${counts.pending} / ${counts.failed}`} color="text-yellow-600" dot="bg-yellow-500" />
            <KpiCard label="Databases"    value={pages.filter(p => p.type === "Database").length} color="text-primary" />
          </div>

          {/* Search + filter */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input type="text" value={search} onChange={e => changeSearch(e.target.value)} placeholder="Search pages…"
                className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white" />
              {search && <button onClick={() => changeSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={13} /></button>}
            </div>
            <div className="flex items-center gap-1 bg-white border rounded-lg p-1 text-sm shrink-0">
              {["all", "Trained", "Pending", "Failed"].map(f => (
                <button key={f} onClick={() => changeFilter(f)} className={`px-3 py-1 rounded-md transition ${filter === f ? "bg-primary text-white" : "text-gray-600 hover:bg-gray-100"}`}>
                  {f === "all" ? "All" : f}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk bar */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
              <span className="text-sm font-medium text-primary">{selectedIds.length} selected</span>
              <div className="flex gap-2 ml-auto">
                <button onClick={handleBulkRetrain} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50">
                  <FiRefreshCw size={12} /> Retrain Selected
                </button>
                <button onClick={handleBulkDelete} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                  <FiTrash2 size={12} /> Delete Selected
                </button>
                <button onClick={() => setSelectedIds([])} className="text-gray-400 hover:text-gray-600 p-1"><FiX size={14} /></button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="w-10 pl-4 py-3"><input type="checkbox" checked={allPageSel} onChange={toggleAllSel} className="accent-primary" /></th>
                    <th className="px-3 py-3 text-left font-semibold">Title</th>
                    <th className="py-3 text-left font-semibold">Type</th>
                    <th className="py-3 text-left font-semibold">Status</th>
                    <th className="py-3 text-left font-semibold">Last Trained</th>
                    <th className="py-3 pr-4 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginated.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-16">
                      <SiNotion size={28} className="mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">{search || filter !== "all" ? "No pages match your filters." : "No pages synced yet."}</p>
                      {!search && filter === "all" && <button onClick={handleSync} className="mt-3 text-sm text-primary hover:underline">Sync from Notion</button>}
                    </td></tr>
                  ) : paginated.map(p => (
                    <tr key={p.id} className={`hover:bg-gray-50 transition ${selectedIds.includes(p.id) ? "bg-primary/5" : ""}`}>
                      <td className="pl-4 py-3"><input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSel(p.id)} className="accent-primary" /></td>
                      <td className="px-3 py-3 font-medium text-gray-800">{p.title}</td>
                      <td className="py-3 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${p.type === "Database" ? "bg-indigo-50 text-indigo-600" : "bg-gray-100 text-gray-600"}`}>{p.type}</span>
                      </td>
                      <td className="py-3 pr-3"><StatusBadge status={p.status} /></td>
                      <td className="py-3 pr-3 text-gray-500 text-xs whitespace-nowrap">{p.lastTrained}</td>
                      <td className="py-3 pr-4">
                        <div className="flex justify-end gap-2">
                          {p.status === "Processing" ? (
                            <span className="flex items-center gap-1.5 text-xs text-blue-600 px-3 py-1.5">
                              <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /> Training…
                            </span>
                          ) : (
                            <>
                              <button onClick={() => handleRetrain(p.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"><FiRefreshCw size={12} /> Retrain</button>
                              <button onClick={() => handleDelete(p.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"><FiTrash2 size={12} /> Remove</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <p>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} pages</p>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}

function KpiCard({ label, value, color, dot }) {
  return (
    <div className="bg-white border rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        {dot && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />}
        <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
